<modal-dialog #editUserModal [dialogTemplate]="editUserDialogTemplate"></modal-dialog>
<ng-template #editUserDialogTemplate>
  <div class="light-content-panel light-control-host" (keydown.escape)="close()" tabindex="0">
    <div class="modal-header">
      <h2 class="panel-title margin-left">Edit User</h2>
      <button type="button" class="modal-close-btn" (click)="close()">
        <svg lucideX></svg>
      </button>
    </div>
    <form [formGroup]="userForm" (ngSubmit)="onSave()" (keydown.enter)="$event.preventDefault()">
      <div class="modal-body">
        <!-- User Information Section -->
        <div class="form-section">
          <h4>User Information</h4>
          <div class="flex-row gap-4">
            <div class="form-row flex-1">
              <div class="form-group">
                <label class="form-label">Name</label>
                <input
                  type="text"
                  formControlName="name"
                  class="arpio-input full-width"
                  placeholder="User's full name"
                  [readonly]="!canEditName"
                  [disabled]="!canEditName"
                />
                @if (nameErrors) {
                  <div class="input-error-text">
                    {{ nameErrors }}
                  </div>
                }
                @if (isSamlUser()) {
                  <div class="input-help-text text-xs" data-cy="edit-user-name-help">
                    Name is managed by your SSO provider and cannot be changed here.
                  </div>
                } @else if (!isSelf) {
                  <div class="input-help-text text-xs" data-cy="edit-user-name-help">
                    You can only change the name of your own user.
                  </div>
                }
              </div>
            </div>
            <div class="form-row flex-1">
              <div class="form-group flex-1">
                <label class="form-label">Email</label>
                <input
                  type="email"
                  formControlName="email"
                  class="arpio-input full-width"
                  placeholder="user@example.com"
                  readonly
                />
                @if (emailErrors) {
                  <div class="input-error-text">
                    {{ emailErrors }}
                  </div>
                }
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="form-label">Identity Provider</label>
              <input class="arpio-input full-width" readonly disabled [value]="user?.identityProviderName || 'Arpio'" />
            </div>
          </div>
        </div>
        <hr />

        <!-- Role and Group Assignment Section -->
        <div class="form-section margin-top">
          @if (accountService.rbacFullEnabled) {
            <h4>Role & Group Assignment</h4>
          }
          <div class="role-group-assignment-description">
            @if (accountService.rbacFullEnabled) {
              <p class="heading-sub-p-text">
                Assign roles directly or through groups to define what this user can view or change in Arpio.
                <br />
                <em
                  >Roles inherited from groups are indicated by
                  <svg lucideUsers class="half-margin-left text-xs margin-right"></svg>
                  SSO roles/groups are indicated by
                  <svg lucideIdCard class="half-margin-left text-xs"></svg
                ></em>
              </p>
            }
          </div>

          <!-- Tables Section -->
          <div
            class="flex justify-between margin-top"
            [ngStyle]="{ gap: accountService.rbacFullEnabled ? '2rem' : '0' }"
          >
            <!-- Assign Roles Table -->
            <div
              class="table-container"
              [ngStyle]="{ flex: accountService.rbacFullEnabled ? '1' : 'auto' }"
              style="width: 100%"
            >
              <div class="flex-row items-center gap-4 margin-bottom">
                <h3 class="whitespace-nowrap">Assign Roles</h3>
                <div class="filter-container">
                  @if (isModalOpen()) {
                    <input
                      pInputText
                      type="text"
                      #rolesFilter
                      (input)="filterRoles($event)"
                      placeholder="Filter roles..."
                      class="arpio-input filter-input"
                    />
                  }
                </div>
              </div>
              @if (isModalOpen()) {
                <p-table
                  #rolesTable
                  [value]="availableRoles"
                  [globalFilterFields]="['name', 'accessLevel']"
                  dataKey="id"
                  [scrollable]="true"
                  scrollHeight="300px"
                  [tableStyle]="{ 'min-width': '20rem' }"
                  class="p-datatable-sm"
                  [loading]="loadingRoles"
                  [customSort]="true"
                  (sortFunction)="customSort($event)"
                >
                  <ng-template #header>
                    <tr>
                      <th class="checkbox-column">
                        <input
                          type="checkbox"
                          class="arpio-checkbox"
                          [checked]="areAllRolesSelected()"
                          [indeterminate]="areSomeRolesSelected()"
                          (change)="toggleAllRoles($event)"
                        />
                      </th>
                      <th pSortableColumn="name" class="role-name-column">
                        Role Name <p-sort-icon field="name"></p-sort-icon>
                      </th>
                      <th pSortableColumn="accessLevel">
                        Access Level(s) <p-sort-icon field="accessLevel"></p-sort-icon>
                      </th>
                    </tr>
                  </ng-template>
                  <ng-template #body let-role>
                    <tr
                      [ngClass]="{
                        'default-role-row': isDefaultAccountRole(role),
                        'group-inherited-role': role.source === 'group' || role.source === 'sso',
                      }"
                    >
                      <td>
                        <input
                          type="checkbox"
                          class="arpio-checkbox"
                          [checked]="isRoleSelected(role) || role.source === 'group' || role.source === 'sso'"
                          [disabled]="role.source === 'group' || role.source === 'sso'"
                          (change)="toggleRole(role, $event)"
                        />
                      </td>
                      <td class="role-name-column">
                        <div>
                          {{ role.name }}
                          @if (role.source === 'group') {
                            <svg
                              lucideUsers
                              class="text-muted margin-left text-xs"
                              [pTooltip]="getGroupInheritanceTooltip(role.groupNames || [])"
                              tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                              tooltipPosition="top"
                              [autoHide]="false"
                              [hideDelay]="200"
                              [escape]="false"
                              tooltipEvent="hover"
                            ></svg>
                          }
                          @if (role.source === 'sso') {
                            <svg
                              lucideIdCard
                              class="text-muted margin-left text-xs"
                              pTooltip="Provided by SSO provider"
                              tooltipStyleClass="arp-tooltip"
                              tooltipPosition="top"
                            ></svg>
                          }
                          @if (role.source === 'direct' && role.groupNames && role.groupNames.length > 0) {
                            <span
                              class="text-xs text-muted half-margin-left"
                              [pTooltip]="getGroupInheritanceTooltip(role.groupNames, 'Also inherited from group(s):')"
                              tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                              tooltipPosition="top"
                              [autoHide]="false"
                              [hideDelay]="200"
                              [escape]="false"
                              tooltipEvent="hover"
                            >
                              (+ groups)
                            </span>
                          }
                        </div>
                      </td>
                      <td>
                        @if (role.accessLevels && role.accessLevels.length > 0) {
                          <div class="access-level-badges">
                            @for (accessLevelInfo of role.accessLevels; track accessLevelInfo) {
                              <p-tag
                                [severity]="
                                  accessLevelInfo.accessLevel.includes('admin')
                                    ? 'danger'
                                    : accessLevelInfo.accessLevel.includes('read')
                                      ? 'warning'
                                      : 'info'
                                "
                                class="access-level-badge fit-width"
                                [pTooltip]="getAccessLevelTooltip(accessLevelInfo)"
                                tabindex="0"
                                role="note"
                                [attr.aria-label]="getAccessLevelTooltip(accessLevelInfo)"
                                tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                                tooltipPosition="top"
                                [autoHide]="false"
                                [hideDelay]="200"
                                [escape]="false"
                                tooltipEvent="hover"
                                value="{{ accessLevelService.getAccessLevelDisplayName(accessLevelInfo.accessLevel) }}"
                              >
                              </p-tag>
                            }
                          </div>
                        } @else {
                          <div>{{ accessLevelService.getAccessLevelDisplayName(role.accessLevel) }}</div>
                        }
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template #emptymessage>
                    <tr>
                      <td colspan="3" class="empty-state-cell">
                        <div class="empty-state-content">
                          <p class="empty-state-message">No roles available.</p>
                          <p class="empty-state-submessage">Create roles in Role Management to assign them to users.</p>
                        </div>
                      </td>
                    </tr>
                  </ng-template>
                  <ng-template #summary>
                    <div class="flex align-items-center justify-content-between">
                      <span
                        >Displaying {{ rolesTable.filteredValue?.length ?? rolesTable.value?.length }} of
                        {{ rolesTable.value?.length }} roles</span
                      >
                    </div>
                  </ng-template>
                </p-table>
              }
            </div>

            <!-- Assign Groups Table -->
            @if (accountService.rbacFullEnabled) {
              <div class="table-container" style="flex: 1">
                <div class="flex-row items-center gap-4 margin-bottom">
                  <h3 class="whitespace-nowrap">Assign Groups</h3>
                  <div class="filter-container">
                    @if (isModalOpen()) {
                      <input
                        pInputText
                        type="text"
                        #groupsFilter
                        (input)="filterGroups($event)"
                        placeholder="Filter groups..."
                        class="arpio-input filter-input"
                      />
                    }
                  </div>
                </div>
                @if (isModalOpen()) {
                  <p-table
                    #groupsTable
                    [value]="availableGroups"
                    [globalFilterFields]="['name', 'roleCount']"
                    dataKey="id"
                    [scrollable]="true"
                    scrollHeight="300px"
                    [tableStyle]="{ 'min-width': '20rem' }"
                    class="p-datatable-sm"
                  >
                    <ng-template #header>
                      <tr>
                        <th class="checkbox-column">
                          <input
                            type="checkbox"
                            class="arpio-checkbox"
                            [checked]="areAllGroupsSelected()"
                            [indeterminate]="areSomeGroupsSelected()"
                            (change)="toggleAllGroups($event)"
                          />
                        </th>
                        <th pSortableColumn="name">Group Name <p-sort-icon field="name"></p-sort-icon></th>
                        <th pSortableColumn="roleCount">Roles <p-sort-icon field="roleCount"></p-sort-icon></th>
                      </tr>
                    </ng-template>
                    <ng-template #body let-group>
                      <tr [ngClass]="{ 'group-inherited-role': group.source === 'sso' }">
                        <td class="checkbox-column">
                          <input
                            type="checkbox"
                            class="arpio-checkbox"
                            [checked]="isGroupSelected(group) || group.source === 'sso'"
                            [disabled]="group.source === 'sso'"
                            (change)="toggleGroup(group, $event)"
                          />
                        </td>
                        <td>
                          <div>
                            {{ group.name }}
                            @if (group.source === 'sso') {
                              <svg
                                lucideIdCard
                                class="text-muted margin-left text-xs"
                                pTooltip="Provided by SSO provider"
                                tooltipStyleClass="arp-tooltip"
                                tooltipPosition="top"
                              ></svg>
                            }
                          </div>
                        </td>
                        <td>{{ group.roleCount }}</td>
                      </tr>
                    </ng-template>
                    <ng-template #emptymessage>
                      <tr>
                        <td colspan="3" class="empty-state-cell">
                          <div class="empty-state-content">
                            <p class="empty-state-message">No groups available.</p>
                            <p class="empty-state-submessage">
                              Create groups in Group Management to assign them to users.
                            </p>
                          </div>
                        </td>
                      </tr>
                    </ng-template>
                    <ng-template #summary>
                      <div class="flex align-items-center justify-content-between">
                        <span
                          >Displaying {{ groupsTable.filteredValue?.length ?? groupsTable.value?.length }} of
                          {{ groupsTable.value?.length }} groups</span
                        >
                      </div>
                    </ng-template>
                  </p-table>
                }
              </div>
            }
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="arpio-secondary-button" data-cy="edit-user-cancel-button" (click)="close()">
          Cancel
        </button>
        <button type="submit" class="arpio-button" [disabled]="!canSave || saving">
          @if (saving) {
            <svg lucideLoaderCircle class="lucide-spin"></svg>
          }
          {{ getSaveButtonText() }}
        </button>
      </div>
    </form>
  </div>
</ng-template>
